import { SSO_RETURN_URL_KEY } from 'constants/sso'
import type { PropsWithChildren } from 'react'
import * as React from 'react'
import type AuthService from 'services/auth'

type AuthUser = {
  id: number
  firstname: string
  lastname: string
  profile: number
  reference: string
  username: string
  roles: string[]
  perimeter: {
    'n-X': boolean
    'n-1': boolean
  }
  exp?: number
  iat?: number
  ip?: string
}

type AuthContextValue = {
  auth: AuthService
  bypassFrom: boolean
  login: (token: string) => void
  logout: () => void
  loginFromToken: (token: string) => void
  refreshToken: (token: string) => void
  user: AuthUser | null | undefined
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined)

interface AuthProviderProps extends PropsWithChildren {
  auth: AuthService
}

export default function AuthProvider({ auth, children }: AuthProviderProps) {
  const [user, setUser] = React.useState<AuthUser | null | undefined>()
  // When clicking logout we do not want the `?from=...` part
  // @see https://gitlab.com/eleo-rh/training/-/issues/8669
  const [bypassFrom, setBypassFrom] = React.useState(false)

  const logout = React.useCallback(() => {
    auth.logout()
  }, [auth])

  const loginFromToken = React.useCallback(
    (token: string) => {
      auth.login(token)
      setUser((auth.getUser() as AuthUser | null) ?? null)
      setBypassFrom(false)
    },
    [auth],
  )

  /**
   * SSO and Old Portal app come to this app with their own token
   * @see containers/Sso
   */
  const login = React.useCallback(
    (token: string) => {
      loginFromToken(token)
      setUser((auth.getUser() as AuthUser | null) ?? null)
    },
    [auth, loginFromToken],
  )

  const refreshToken = React.useCallback(
    (token: string) => {
      auth.login(token)
      // setUser(auth.getUser())
    },
    [auth],
  )

  // First render
  React.useEffect(() => {
    const hasValidToken = auth.validToken()

    if (hasValidToken) {
      setUser((auth.getUser() as AuthUser | null) ?? null)
      return () => {}
    }

    if (typeof window !== 'undefined') {
      const { pathname, search, hash } = window.location
      const lowerPath = pathname?.toLowerCase?.() || ''
      const isAuthRoute = lowerPath.includes('/login') || lowerPath.includes('/reset-password')
      const isSsoRoute = pathname === '/sso'

      if (!isAuthRoute && !isSsoRoute) {
        // Persist the original location before we wipe credentials here.
        // AuthProvider sits very early in the tree (index.tsx) so it is the first
        // place that detects "no valid token" for both SSO and classic auth flows.
        // Stashing the route here guarantees every entry point records it once.
        const intendedUrl = `${pathname}${search}${hash}`
        if (intendedUrl) {
          window.sessionStorage.setItem(SSO_RETURN_URL_KEY, intendedUrl)
        }
      }
    }

    auth.logout()

    return () => {}
  }, [auth])

  const value = React.useMemo<AuthContextValue>(
    () => ({
      auth,
      bypassFrom,
      login,
      logout,
      loginFromToken,
      refreshToken,
      user,
    }),
    [auth, bypassFrom, login, logout, loginFromToken, refreshToken, user],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    throw new Error('AuthContext must be used within a Provider')
  }
  return context
}

export type { AuthContextValue, AuthUser }
